/**
 * Utility functions for managing assessment data in localStorage
 */

/**
 * Generate a unique storage key for a candidate's assessment
 */
export const getAssessmentStorageKey = (candidateId: string) => `assessment_${candidateId}`;

/**
 * Save assessment progress to localStorage
 */
export const saveAssessmentProgress = (
  candidateId: string,
  answers: Record<string, string>,
  currentQuestionIndex: number,
  assessmentId: string
) => {
  if (!candidateId) return;
  
  const data = {
    answers,
    currentQuestionIndex,
    timestamp: new Date().getTime(),
    assessmentId, // Save the assessment ID to validate when loading
  };
  localStorage.setItem(getAssessmentStorageKey(candidateId), JSON.stringify(data));
};

/**
 * Load assessment progress from localStorage
 * Returns null if no valid data is found
 */
export const loadAssessmentProgress = (candidateId: string, assessmentId: string) => {
  if (!candidateId) return null;
  
  try {
    const storedData = localStorage.getItem(getAssessmentStorageKey(candidateId));
    if (!storedData) return null;
    
    const data = JSON.parse(storedData);
    
    // Validate the stored data
    if (data.assessmentId !== assessmentId) {
      // Assessment ID mismatch, clear the stored data
      localStorage.removeItem(getAssessmentStorageKey(candidateId));
      return null;
    }
    
    // Check if the data is less than 24 hours old
    const now = new Date().getTime();
    const storedTime = data.timestamp || 0;
    const hoursPassed = (now - storedTime) / (1000 * 60 * 60);
    
    if (hoursPassed > 24) {
      // Data is too old, clear it
      localStorage.removeItem(getAssessmentStorageKey(candidateId));
      return null;
    }
    
    return {
      answers: data.answers || {},
      currentQuestionIndex: data.currentQuestionIndex || 0,
    };
  } catch (error) {
    console.error("Error loading assessment data from localStorage:", error);
    return null;
  }
};

/**
 * Clear assessment progress from localStorage
 */
export const clearAssessmentProgress = (candidateId: string) => {
  if (!candidateId) return;
  localStorage.removeItem(getAssessmentStorageKey(candidateId));
};
